import {
	Button,
	ChevDownIcon,
	ChevUpIcon,
	CloseIcon,
	IaIcon,
	Text,
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@viapeople/components'
import { useState } from 'react'

export type SuggestionCategory = 'clarity' | 'specificity' | 'tone' | 'actionability' | 'structure' | 'grammar'

export type FeedbackSuggestion = {
	id: string
	category?: SuggestionCategory
	title: string
	description: string
	proposedRewrite: string
	rationale: string
}

const CATEGORY_META: Record<SuggestionCategory, { label: string; color: string }> = {
	clarity: { label: 'Clarity', color: 'bg-blue-100 text-blue-800' },
	specificity: { label: 'Specificity', color: 'bg-amber-100 text-amber-800' },
	tone: { label: 'Tone', color: 'bg-purple-100 text-purple-800' },
	actionability: { label: 'Actionability', color: 'bg-green-100 text-green-800' },
	structure: { label: 'Structure', color: 'bg-cyan-100 text-cyan-800' },
	grammar: { label: 'Grammar', color: 'bg-rose-100 text-rose-800' },
}

const CategoryBadge = ({ category }: { category: SuggestionCategory }) => {
	const meta = CATEGORY_META[category]
	return (
		<span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${meta.color}`}>
			{meta.label}
		</span>
	)
}

const SuggestionCard = ({
	suggestion,
	onApply,
	onDismiss,
}: {
	suggestion: FeedbackSuggestion
	onApply: () => void
	onDismiss: () => void
}) => {
	const [isExpanded, setIsExpanded] = useState(false)
	const category = suggestion.category ?? 'clarity'

	return (
		<div className='rounded-lg border border-neutral-200 bg-white transition-shadow hover:shadow-sm'>
			<div className='flex items-start gap-3 p-3'>
				<div className='min-w-0 flex-1'>
					<div className='flex items-center gap-2'>
						<CategoryBadge category={category} />
						<Text variant='xsmall' weight='semibold' className='truncate'>
							{suggestion.title}
						</Text>
					</div>
					<Text variant='hint' className='mt-1 text-neutral-600'>
						{suggestion.description}
					</Text>
				</div>

				<div className='flex shrink-0 items-center gap-1'>
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								type='button'
								onClick={() => setIsExpanded((prev) => !prev)}
								className='inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700'>
								{isExpanded ? (
									<ChevUpIcon size={14} className='fill-current' />
								) : (
									<ChevDownIcon size={14} className='fill-current' />
								)}
							</button>
						</TooltipTrigger>
						<TooltipContent>{isExpanded ? 'Hide preview' : 'Preview rewrite'}</TooltipContent>
					</Tooltip>
					<Tooltip>
						<TooltipTrigger asChild>
							<button
								type='button'
								onClick={onDismiss}
								className='inline-flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600'>
								<CloseIcon size={14} className='fill-current' />
							</button>
						</TooltipTrigger>
						<TooltipContent>Dismiss</TooltipContent>
					</Tooltip>
				</div>
			</div>

			{isExpanded && (
				<div className='border-t border-neutral-100 px-3 pb-3 pt-2'>
					<Text variant='hint' weight='semibold' className='mb-1 text-neutral-500'>
						Suggested rewrite
					</Text>
					<div className='rounded-md bg-neutral-50 p-2.5'>
						<Text variant='xsmall' className='whitespace-pre-wrap text-neutral-800'>
							{suggestion.proposedRewrite}
						</Text>
					</div>
					<Text variant='hint' className='mt-2 italic text-neutral-400'>
						{suggestion.rationale}
					</Text>
				</div>
			)}

			<div className='border-t border-neutral-100 px-3 py-2'>
				<Button size='small' variant='primary' onClick={onApply}>
					Apply suggestion
				</Button>
			</div>
		</div>
	)
}

export const FeedbackSuggestionsLoading = () => (
	<div className='mt-3 rounded-lg border border-primary-200 bg-primary-50/40 p-4'>
		<div className='flex items-center gap-2'>
			<IaIcon size={18} className='animate-pulse fill-primary-600' />
			<Text variant='xsmall' weight='semibold' className='text-primary-700'>
				Analyzing your feedback...
			</Text>
		</div>
		<div className='mt-3 space-y-2'>
			{[1, 2, 3].map((i) => (
				<div key={i} className='animate-pulse rounded-lg border border-neutral-100 bg-white p-3'>
					<div className='flex items-center gap-2'>
						<div className='h-5 w-16 rounded-full bg-neutral-200' />
						<div className='h-4 w-40 rounded bg-neutral-200' />
					</div>
					<div className='mt-2 h-3 w-full rounded bg-neutral-100' />
					<div className='mt-1 h-3 w-3/4 rounded bg-neutral-100' />
				</div>
			))}
		</div>
	</div>
)

export const FeedbackSuggestionsPanel = ({
	suggestions,
	onApply,
	onDismiss,
	onClose,
}: {
	suggestions: FeedbackSuggestion[]
	onApply: (suggestion: FeedbackSuggestion) => void
	onDismiss: (suggestion: FeedbackSuggestion) => void
	onClose: () => void
}) => {
	if (!suggestions.length) {
		return null
	}

	return (
		<div className='mt-3 rounded-lg border border-primary-200 bg-primary-50/40 p-4'>
			<div className='mb-3 flex items-center justify-between'>
				<div className='flex items-center gap-2'>
					<IaIcon size={18} className='fill-primary-600' />
					<Text variant='xsmall' weight='semibold' className='text-primary-700'>
						AI Suggestions
					</Text>
					<span className='inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-600 px-1.5 text-xs font-medium text-white'>
						{suggestions.length}
					</span>
				</div>
				<button
					type='button'
					onClick={onClose}
					className='inline-flex cursor-pointer items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700'>
					<CloseIcon size={12} className='fill-current' />
					Close all
				</button>
			</div>

			<div className='space-y-2'>
				{suggestions.map((suggestion) => (
					<SuggestionCard
						key={suggestion.id}
						suggestion={suggestion}
						onApply={() => onApply(suggestion)}
						onDismiss={() => onDismiss(suggestion)}
					/>
				))}
			</div>
		</div>
	)
}
